import React from 'react';
import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
import RecordField from './RecordField';

interface FormData {
  name: string;
  type: string;
  value: string;
  ttl: string;
  priority: string;
  weight: string;
  port: string;
}

interface RecordEditorFormProps {
  formData: FormData;
  errors: Record<string, string>;
  isSubmitting: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onInputChange: (field: string, value: string) => void;
  onCancel: () => void;
  getPlaceholder: (type: string) => string;
}

const RECORD_TYPES = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS', 'SOA', 'SRV', 'CAA', 'PTR'];

const RecordEditorForm: React.FC<RecordEditorFormProps> = ({
  formData,
  errors,
  isSubmitting,
  onSubmit,
  onInputChange,
  onCancel,
  getPlaceholder,
}) => {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <RecordField
          label="Name"
          value={formData.name}
          onChange={(value) => onInputChange('name', value)}
          error={errors.name}
          placeholder="@ for root domain"
          required
        />
        
        <RecordField
          label="Type"
          value={formData.type}
          onChange={(value) => onInputChange('type', value)}
          error={errors.type}
          type="select"
          options={RECORD_TYPES}
          required
        />
      </div>

      <RecordField
        label="Value"
        value={formData.value}
        onChange={(value) => onInputChange('value', value)}
        error={errors.value}
        placeholder={getPlaceholder(formData.type)}
        required
      />

      <div className="grid grid-cols-2 gap-4">
        <RecordField
          label="TTL (seconds)"
          value={formData.ttl}
          onChange={(value) => onInputChange('ttl', value)}
          error={errors.ttl}
          type="number"
          min="1"
          required
        />

        {(formData.type === 'MX' || formData.type === 'SRV') && (
          <RecordField
            label="Priority"
            value={formData.priority}
            onChange={(value) => onInputChange('priority', value)}
            error={errors.priority}
            type="number"
            min="0"
            required={formData.type === 'SRV'}
          />
        )}
      </div>

      {formData.type === 'SRV' && (
        <div className="grid grid-cols-2 gap-4">
          <RecordField
            label="Weight"
            value={formData.weight}
            onChange={(value) => onInputChange('weight', value)}
            error={errors.weight}
            type="number"
            min="0"
            required
          />
          
          <RecordField
            label="Port"
            value={formData.port}
            onChange={(value) => onInputChange('port', value)}
            error={errors.port}
            type="number"
            min="1"
            max="65535"
            required
          />
        </div>
      )}

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <motion.button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          disabled={isSubmitting}
        >
          <X className="w-4 h-4 mr-2 inline" />
          Cancel
        </motion.button>
        
        <motion.button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </div>
          ) : (
            <>
              <Check className="w-4 h-4 mr-2 inline" />
              Save Changes
            </>
          )}
        </motion.button>
      </div>
    </form>
  );
};

export default RecordEditorForm;